import { useGetEffect } from '@/services/adapters/useGetEffect'
import { useGetEffects } from '@/services/adapters/useGetEffects'
import { useGetLayerPacks } from '@/services/adapters/useGetLayerPacks'
import {
  IBaseEffect,
  IBaseEffectReference,
  IEffect,
  IEffectReference,
  IEffectRemedy,
  ILayerPack,
  IRemedy
} from '@lifk/types'
import { computed, ComputedRef, inject, InjectionKey, provide } from 'vue'

type TFullEffectsStore = {
  fullEffects: ComputedRef<IEffect[]>
  effectReference: ComputedRef<IEffectReference | undefined>
  effectsByKey: ComputedRef<Record<string, IBaseEffect>>
}
export const FullEffectsKey = Symbol() as InjectionKey<TFullEffectsStore>

export const provideFullEffects = (fullRemedies: ComputedRef<IRemedy[]>) => {
  const { data: effects } = useGetEffects()
  const { data: effect } = useGetEffect()
  const { data: layerPacks } = useGetLayerPacks()

  const getLayerPackForEffect = (
    effect: IBaseEffect | IBaseEffectReference
  ): ILayerPack | undefined =>
    layerPacks.value?.find((lp) => lp.effect === effect.key)

  const fullEffects: ComputedRef<IEffect[]> = computed(() =>
    (effects.value ?? []).map((effect) => ({
      ...effect,
      layerPack: getLayerPackForEffect(effect)
    }))
  )

  const effectReference: ComputedRef<IEffectReference | undefined> = computed(
    () => {
      if (!effect.value) return

      const effectRemedies: IEffectRemedy[] = []
      fullRemedies.value.forEach((remedy) => {
        const remedyEffect = remedy.effects.find(
          (e) => e.key === effect.value?.key
        )
        if (remedyEffect) {
          effectRemedies.push({
            ...remedy,
            level: remedyEffect.level,
            points: remedyEffect.points
          })
        }
      })

      return {
        ...effect.value,
        layerPack: getLayerPackForEffect(effect.value),
        remedies: effectRemedies
      }
    }
  )

  // For quick lookup by Layer Packs
  const effectsByKey: ComputedRef<Record<string, IBaseEffect>> = computed(() =>
    (effects.value ?? []).reduce((acc: Record<string, IBaseEffect>, effect) => {
      acc[effect.key] = effect
      return acc
    }, {})
  )

  provide(FullEffectsKey, { fullEffects, effectReference, effectsByKey })
}

export default function useFullEffects(): TFullEffectsStore {
  return inject(FullEffectsKey)!
}
